import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Send, CheckCircle2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLang } from "@/i18n";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactForm() {
  const { lang, t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", property_type: "", service_type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, lang });
      setSent(true);
      toast.success(t.contact.successToast);
    } catch {
      toast.error(t.contact.errorToast);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-[#0E1B2A]/12 bg-[#FAF8F5] px-4 py-3 text-sm text-[#0E1B2A] placeholder:text-[#94A3B8] transition-all duration-300 focus:border-[#C87A57] focus:ring-2 focus:ring-[#C87A57]/20";

  return (
    <section id="contact" className="bg-[#FAF8F5] py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-[1fr_1.2fr] lg:gap-20">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C87A57]">{t.contact.overline}</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight tracking-tight text-[#0E1B2A]">
              {t.contact.title}
            </h2>
            <p className="mt-4 max-w-md text-base sm:text-lg text-[#475569]">{t.contact.sub}</p>

            <div className="mt-10 space-y-5">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#0E1B2A]/50">{t.contact.directTitle}</p>
              <a data-testid="contact-address" href="https://maps.google.com/?q=13+rue+Berthe+Morisot+06200+Nice" target="_blank" rel="noreferrer" className="flex items-start gap-4 group">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#2C5E7A]/10 text-[#2C5E7A] transition-colors duration-300 group-hover:bg-[#2C5E7A] group-hover:text-white">
                  <MapPin size={18} />
                </span>
                <span className="pt-2.5 text-sm sm:text-base font-medium text-[#0E1B2A]">{t.contact.address}</span>
              </a>
              <a data-testid="contact-phone" href="tel:+33767473293" className="flex items-start gap-4 group">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#2C5E7A]/10 text-[#2C5E7A] transition-colors duration-300 group-hover:bg-[#2C5E7A] group-hover:text-white">
                  <Phone size={18} />
                </span>
                <span className="pt-2.5 text-sm sm:text-base font-medium text-[#0E1B2A]">07 67 47 32 93</span>
              </a>
              <a data-testid="contact-email" href="mailto:topserv06@gmail.com" className="flex items-start gap-4 group">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#2C5E7A]/10 text-[#2C5E7A] transition-colors duration-300 group-hover:bg-[#2C5E7A] group-hover:text-white">
                  <Mail size={18} />
                </span>
                <span className="pt-2.5 text-sm sm:text-base font-medium text-[#0E1B2A]">topserv06@gmail.com</span>
              </a>
              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#D4AF37]/12 text-[#8B6E10]">
                  <Clock size={18} />
                </span>
                <span className="pt-2.5 text-sm sm:text-base font-medium text-[#0E1B2A]">{t.contact.hours}</span>
              </div>
            </div>

            <div data-testid="contact-map" className="mt-8 overflow-hidden rounded-2xl border border-[#0E1B2A]/10 shadow-[0_15px_35px_-15px_rgba(14,27,42,0.2)]">
              <iframe
                title="Service Plus — 13 rue Berthe Morisot, 06200 Nice Ouest"
                src="https://www.google.com/maps?q=13+rue+Berthe+Morisot,+06200+Nice,+France&output=embed"
                className="h-64 w-full"
                loading="lazy"
              />
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="rounded-3xl border border-[#0E1B2A]/8 bg-white p-7 shadow-[0_25px_60px_-20px_rgba(14,27,42,0.15)] sm:p-10">
              {sent ? (
                <div data-testid="form-success-message" className="flex flex-col items-center py-14 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#D4AF37]/15">
                    <CheckCircle2 size={32} className="text-[#8B6E10]" />
                  </span>
                  <h3 className="mt-6 font-serif text-2xl sm:text-3xl font-semibold text-[#0E1B2A]">{t.contact.successTitle}</h3>
                  <p className="mt-3 max-w-sm text-sm sm:text-base text-[#475569]">{t.contact.successText}</p>
                  <button
                    data-testid="form-again-btn"
                    onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", property_type: "", service_type: "", message: "" }); }}
                    className="mt-8 text-sm font-semibold text-[#2C5E7A] underline-offset-4 hover:underline"
                  >
                    {t.contact.again}
                  </button>
                </div>
              ) : (
                <form data-testid="quote-form" onSubmit={submit} className="space-y-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="qf-name" className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-[#0E1B2A]/60">{t.contact.name}</label>
                      <input id="qf-name" data-testid="input-full-name" required minLength={2} value={form.name} onChange={set("name")} placeholder={t.contact.namePh} className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="qf-phone" className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-[#0E1B2A]/60">{t.contact.phone}</label>
                      <input id="qf-phone" data-testid="input-phone" required minLength={6} value={form.phone} onChange={set("phone")} placeholder={t.contact.phonePh} className={inputCls} />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="qf-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-[#0E1B2A]/60">{t.contact.email}</label>
                    <input id="qf-email" data-testid="input-email" type="email" required value={form.email} onChange={set("email")} placeholder={t.contact.emailPh} className={inputCls} />
                  </div>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-[#0E1B2A]/60">{t.contact.property}</label>
                      <Select required value={form.property_type} onValueChange={set("property_type")}>
                        <SelectTrigger data-testid="select-property-type" className={inputCls}>
                          <SelectValue placeholder={t.contact.propertyPh} />
                        </SelectTrigger>
                        <SelectContent>
                          {t.contact.propertyOptions.map((o) => (
                            <SelectItem key={o} value={o}>{o}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-[#0E1B2A]/60">{t.contact.service}</label>
                      <Select value={form.service_type} onValueChange={set("service_type")}>
                        <SelectTrigger data-testid="select-service-type" className={inputCls}>
                          <SelectValue placeholder={t.contact.servicePh} />
                        </SelectTrigger>
                        <SelectContent>
                          {t.contact.serviceOptions.map((o) => (
                            <SelectItem key={o} value={o}>{o}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="qf-message" className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-[#0E1B2A]/60">{t.contact.message}</label>
                    <textarea id="qf-message" data-testid="input-message" required minLength={10} rows={4} value={form.message} onChange={set("message")} placeholder={t.contact.messagePh} className={`${inputCls} resize-none`} />
                  </div>
                  <button
                    data-testid="quote-submit-btn"
                    type="submit"
                    disabled={loading}
                    className="group flex w-full items-center justify-center gap-2 rounded-full bg-[#0E1B2A] py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#C87A57] hover:shadow-[0_20px_40px_-12px_rgba(200,122,87,0.5)] disabled:opacity-60"
                  >
                    {loading ? t.contact.sending : t.contact.submit}
                    {!loading && <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
